import { useRef, useState, useEffect } from 'react'
import {
  AlertCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  LoaderCircleIcon,
  PaperclipIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react'
import { router, usePage } from '@inertiajs/react'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { Button } from '@/shared/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { Textarea } from '@/shared/ui/textarea'
import { t } from '@/shared/lib/i18n'
import {
  reportsMonthlyPath,
  reportsMonthlyShowPath,
  reportsMonthlyUndoSignPath,
  reportsMonthlyUsersPdfPath,
  reportsMonthlyRequestPdfPath,
  reportsMonthlyDownloadPdfPath,
  reportsMonthlyRequestXlsxPath,
  reportsMonthlyDownloadXlsxPath,
  reportCorrectiveActionCreatePath,
  reportCorrectiveActionUpdatePath,
  reportCorrectiveActionDiscardPath,
  reportAttachmentCreatePath,
  reportAttachmentDownloadPath,
  reportAttachmentDiscardPath,
} from '@/shared/lib/routes'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import ReportStatusBadge from './components/ReportStatusBadge'
import ReportLiveIndicator from './components/ReportLiveIndicator'
import QaAlertBadge from './components/QaAlertBadge'
import {
  MONTHLY_REPORT_ALERT_CATEGORY_KEYS,
  QA_ALERT_CATEGORIES,
  type AlertCategoryKey,
} from '@/shared/constants/qaAlertConfig'
import SignersChain from './components/SignersChain'
import ReportRejectModal from './components/ReportRejectModal'
import type { SignerStatus } from './components/SignersChain'
import type WebInstrumentTaskReportShow from '@/shared/types/web/WebInstrumentTaskReportShow'
import type { WebInstrumentCalendarView } from '@/shared/types/web'
import InstrumentCalendarViewPanel from '../Instruments/components/ReportsTab/InstrumentCalendarViewPanel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import NcSymbolLegend from './components/NcSymbolLegend'
import SignReportDialog from './components/SignReportDialog'
import type { SharedProps } from '@/shared/types/SharedProps'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'
import { fetchInertiaPartial } from '@/shared/lib/fetchInertiaPartial'

interface MonthlyBar {
  periodLabel: string
  compliancePercent: number | null
}

interface ComplianceTrend {
  currentPercent: number | null
  avgLastThree: number | null
  avgLastSix: number | null
  monthlyBars: MonthlyBar[]
}

interface AlertCounts {
  periodLabel: string
  failureCount: number
  nonComplianceCount: number
  serviceReportCount: number
  dateChangeCount: number
  correctiveActionCount: number
}

interface LicenseUnitSummary {
  unitId: number
  unitName: string
  licenseNumber?: string
  jointCommission?: string
  cap?: string
  cola?: string
  aabb?: string
  aala?: string
  aoa?: string
  ashi?: string
  nyDoh?: string
  caDoh?: string
  flDoh?: string
  nySpfi?: string
  fda?: string
  auId?: string
}

type LicenseFieldKey = Exclude<keyof LicenseUnitSummary, 'unitId' | 'unitName'>

const LICENSE_FIELD_KEYS: LicenseFieldKey[] = [
  'licenseNumber',
  'jointCommission',
  'cap',
  'cola',
  'aabb',
  'aala',
  'aoa',
  'ashi',
  'nyDoh',
  'caDoh',
  'flDoh',
  'nySpfi',
  'fda',
  'auId',
]

interface InstrumentSummaryEntry {
  instrumentId: number | null
  instrumentLabel: string | null
  serialNumber: string | null
  compliancePercent: number | null
}

interface UnitInstrumentsSummary {
  unitId: number
  unitName: string
  instruments: InstrumentSummaryEntry[]
}

interface CorrectiveActionItem {
  id: number
  comment: string
  createdByUserName: string | null
  createdAt: string
  instrumentId: number | null
  instrumentLabel: string | null
}

interface AttachmentItem {
  id: number
  attachmentName: string
  mimeType: string
  attachmentDescription: string | null
  createdAt: string
  timeZoneName?: string | null
}

interface MaintenanceInstrument {
  id: number
  instrumentLabel: string
  serialNumber: string | null
  unitName: string | null
  canTest: boolean
}

interface LotItem {
  id: number
  name: string
}

interface ReportUserRow {
  id: number
  displayName: string
  tasksCompleted: number
  lateLogs: number
  avgCompliancePercent: number | null
}

interface RejectionAlert {
  rejectedBy: string | null
  rejectedAt: string
  comments: string | null
}

interface SignOffHistoryEntry {
  id: number
  userName: string
  userId: number
  occurredAt: string
  isSigned: boolean
  isUndo: boolean
  isValid: boolean
  comments: string | null
}

/** Maps a canonical AlertCategoryKey to the corresponding count field on AlertCounts. */
const alertCountForCategory = (counts: AlertCounts, key: AlertCategoryKey): number => {
  switch (key) {
    case 'failures':
      return counts.failureCount
    case 'nonCompliant':
      return counts.nonComplianceCount
    case 'serviceReports':
      return counts.serviceReportCount
    case 'dateChanges':
      return counts.dateChangeCount
    case 'correctiveActions':
      return counts.correctiveActionCount
    case 'statusChanges':
    case 'departmentMoved':
      // Maintenance Logs calendar KPIs only — monthly AlertCounts has no fields for these.
      return 0
  }
}

const monthlyReportAlertCategories = QA_ALERT_CATEGORIES.filter((config) =>
  MONTHLY_REPORT_ALERT_CATEGORY_KEYS.has(config.key),
)

const monthlyReportTrendCategories = monthlyReportAlertCategories.filter(
  (config) => config.key !== 'correctiveActions',
)

// `stale` = an export file exists but the report is live (in-progress), so the file is
// not a guaranteed-current snapshot and must be regenerated on download (REBUILD-182).
type ExportStatus = 'pending' | 'generating' | 'ready' | 'stale' | 'failed'

interface ExportStatuses {
  pdf: ExportStatus | null
  xlsx: ExportStatus | null
}

interface Props {
  report: WebInstrumentTaskReportShow
  chainSigners: SignerStatus[]
  canSign: boolean
  canUndo: boolean
  canEdit: boolean
  canAddCorrectiveAction: boolean
  canUploadAttachment: boolean
  derivedStatus: { code: string | null; live: boolean; signable: boolean }
  exportStatuses: ExportStatuses
  pdfReady: boolean
  hasConfidentialPdf: boolean
  canViewConfidential: boolean
  complianceTrend?: ComplianceTrend
  alertCountsCurrent?: AlertCounts
  alertCounts3month?: AlertCounts[]
  licenseSummary?: LicenseUnitSummary[]
  unitsInstrumentsSummary?: UnitInstrumentsSummary[]
  correctiveActions: CorrectiveActionItem[]
  attachments: AttachmentItem[]
  canDeleteAttachment: boolean
  maintenanceInstruments?: MaintenanceInstrument[]
  activeInstrumentCalendar?: WebInstrumentCalendarView | null
  activeInstrumentLots?: LotItem[]
  reportUsers?: ReportUserRow[]
  rejectionAlert?: RejectionAlert | null
  signOffHistory: SignOffHistoryEntry[]
  hideEmptyData: boolean
  [key: string]: unknown
}

interface ComplianceBadgeProps {
  percent: number | null
}

const ComplianceBadge = ({ percent }: ComplianceBadgeProps) => {
  if (percent == null) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
        —
      </span>
    )
  }
  const colorClass =
    percent >= 90
      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20'
      : percent >= 70
        ? 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20'
        : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20'
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums ${colorClass}`}
    >
      {Math.round(percent)}%
    </span>
  )
}

interface MonthComplianceCardProps {
  periodLabel: string
  compliancePercent: number | null
  isCurrent: boolean
}

const MonthComplianceCard = ({
  periodLabel,
  compliancePercent,
  isCurrent,
}: MonthComplianceCardProps) => {
  const numericPercent = compliancePercent != null ? Number(compliancePercent) : null

  const accentColor =
    numericPercent == null
      ? 'bg-muted-foreground/30'
      : numericPercent >= 90
        ? 'bg-emerald-500'
        : numericPercent >= 70
          ? 'bg-amber-500'
          : 'bg-red-500'

  const textColor =
    numericPercent == null
      ? 'text-muted-foreground'
      : numericPercent >= 90
        ? 'text-emerald-600'
        : numericPercent >= 70
          ? 'text-amber-600'
          : 'text-red-600'

  return (
    <div
      className={`flex w-24 flex-col items-center gap-1 rounded-xl border px-3 py-3 transition-colors ${isCurrent ? 'border-primary/30 bg-primary/5 shadow-sm' : 'border-border bg-card'}`}
    >
      <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {periodLabel}
      </span>
      <span className={`text-2xl font-bold tabular-nums leading-none ${textColor}`}>
        {numericPercent != null ? `${Math.round(numericPercent)}%` : '—'}
      </span>
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${accentColor}`}
          style={{ width: `${numericPercent != null ? Math.min(numericPercent, 100) : 0}%` }}
        />
      </div>
    </div>
  )
}

interface LicenseUnitRowProps {
  unit: LicenseUnitSummary
}

const LicenseUnitRow = ({ unit }: LicenseUnitRowProps) => {
  const [open, setOpen] = useState(false)

  const presentFields = LICENSE_FIELD_KEYS.filter((key) => unit[key] != null && unit[key] !== '')
  const firstField = presentFields[0]

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-muted/50 transition-colors">
        <span className="font-medium">{unit.unitName}</span>
        <span className="flex items-center gap-2">
          {!open && firstField && (
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
              {t(`reports.monthly.license.${firstField}`)}: {unit[firstField]}
            </span>
          )}
          <ChevronDownIcon
            className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {presentFields.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            {t('reports.monthly.license.noFields')}
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 px-3 py-2 text-sm sm:grid-cols-3">
            {presentFields.map((fieldKey) => (
              <div key={fieldKey}>
                <dt className="text-xs text-muted-foreground">
                  {t(`reports.monthly.license.${fieldKey}`)}
                </dt>
                <dd className="font-medium">{unit[fieldKey]}</dd>
              </div>
            ))}
          </dl>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

interface LicenseSummarySectionProps {
  licenseSummary: LicenseUnitSummary[]
}

const LicenseSummarySection = ({ licenseSummary }: LicenseSummarySectionProps) => {
  const [open, setOpen] = useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <section className="rounded-lg border p-4">
        <CollapsibleTrigger className="flex w-full items-center justify-between">
          <h3 className="font-semibold text-lg">{t('reports.monthly.license.title')}</h3>
          <ChevronDownIcon
            className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-1">
          {licenseSummary.map((unit) => (
            <LicenseUnitRow key={unit.unitId} unit={unit} />
          ))}
        </CollapsibleContent>
      </section>
    </Collapsible>
  )
}

/* ── Unit List — expandable unit rows with instrument drill-down ── */

interface UnitDrillDownRowProps {
  unit: UnitInstrumentsSummary
}

const UnitDrillDownRow = ({ unit }: UnitDrillDownRowProps) => {
  const [expanded, setExpanded] = useState(false)

  const unitComplianceValues = unit.instruments
    .map((instrument) => instrument.compliancePercent)
    .filter((value): value is number => value != null)
  const unitAvgCompliance =
    unitComplianceValues.length > 0
      ? unitComplianceValues.reduce((accumulator, value) => accumulator + value, 0) /
        unitComplianceValues.length
      : null

  return (
    <>
      <tr
        className="border-b hover:bg-muted/30 cursor-pointer select-none"
        onClick={() => setExpanded((previous) => !previous)}
        aria-expanded={expanded}
      >
        <td className="px-3 py-2">
          <span className="flex items-center gap-2">
            <ChevronDownIcon
              className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
            />
            <span className="font-medium">{unit.unitName}</span>
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          <ComplianceBadge percent={unitAvgCompliance} />
        </td>
      </tr>

      {expanded && (
        <>
          {unit.instruments.length === 0 ? (
            <tr className="border-b bg-muted/20">
              <td colSpan={2} className="px-8 py-2 text-xs text-muted-foreground italic">
                {t('reports.monthly.unitList.noInstruments')}
              </td>
            </tr>
          ) : (
            <>
              <tr className="bg-muted/10">
                <th className="px-8 py-1.5 text-left text-xs font-semibold text-muted-foreground">
                  {t('reports.monthly.unitList.colInstrument')}
                </th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-muted-foreground">
                  {t('reports.monthly.unitList.colCompliance')}
                </th>
              </tr>
              {unit.instruments.map((instrument) => (
                <tr
                  key={instrument.instrumentId ?? instrument.instrumentLabel}
                  className="border-b last:border-0 bg-muted/5 hover:bg-muted/20"
                >
                  <td className="px-8 py-1.5 text-xs">
                    <span className="font-medium">{instrument.instrumentLabel ?? '—'}</span>
                    {instrument.serialNumber && (
                      <span className="ml-2 text-muted-foreground">{instrument.serialNumber}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <ComplianceBadge percent={instrument.compliancePercent} />
                  </td>
                </tr>
              ))}
            </>
          )}
        </>
      )}
    </>
  )
}

interface UnitListSectionProps {
  unitsInstrumentsSummary: UnitInstrumentsSummary[]
}

const UnitListSection = ({ unitsInstrumentsSummary }: UnitListSectionProps) => {
  return (
    <section className="space-y-3 rounded-lg border p-4">
      <h3 className="font-semibold text-lg">{t('reports.monthly.unitList.title')}</h3>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50 text-left">
              <th className="px-3 py-2 font-medium">{t('reports.monthly.unitList.colUnit')}</th>
              <th className="px-3 py-2 font-medium text-right">
                {t('reports.monthly.unitList.colCompliance')}
              </th>
            </tr>
          </thead>
          <tbody>
            {unitsInstrumentsSummary.map((unit) => (
              <UnitDrillDownRow key={unit.unitId} unit={unit} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

const CorrectiveActionsSection = ({
  reportId,
  actions,
  canEdit,
}: {
  reportId: number
  actions: CorrectiveActionItem[]
  canEdit: boolean
}) => {
  const { formatDateTime } = useSiteDateTime()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [addingNew, setAddingNew] = useState(false)
  const [commentValue, setCommentValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)

  const startAdd = () => {
    setEditingId(null)
    setCommentValue('')
    setAddingNew(true)
  }

  const startEdit = (action: CorrectiveActionItem) => {
    setAddingNew(false)
    setEditingId(action.id)
    setCommentValue(action.comment)
  }

  const cancelForm = () => {
    setAddingNew(false)
    setEditingId(null)
    setCommentValue('')
  }

  const handleSave = () => {
    if (!commentValue.trim()) return
    setSubmitting(true)

    if (addingNew) {
      router.post(
        reportCorrectiveActionCreatePath(reportId),
        { comment: commentValue.trim() },
        {
          onSuccess: () => {
            setSubmitting(false)
            setAddingNew(false)
            setCommentValue('')
          },
          onError: () => setSubmitting(false),
        },
      )
    } else if (editingId != null) {
      router.patch(
        reportCorrectiveActionUpdatePath(reportId, editingId),
        { comment: commentValue.trim() },
        {
          onSuccess: () => {
            setSubmitting(false)
            setEditingId(null)
            setCommentValue('')
          },
          onError: () => setSubmitting(false),
        },
      )
    }
  }

  const handleDelete = (id: number) => {
    router.delete(reportCorrectiveActionDiscardPath(reportId, id), {
      onFinish: () => setDeleteTargetId(null),
    })
  }

  return (
    <section className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">{t('reports.monthly.correctiveActions.title')}</h3>
        {canEdit && !addingNew && editingId == null && (
          <Button size="sm" variant="outline" onClick={startAdd}>
            {t('reports.monthly.correctiveActions.add')}
          </Button>
        )}
      </div>

      {actions.length === 0 && !addingNew && (
        <p className="text-sm text-muted-foreground">
          {t('reports.monthly.correctiveActions.noItems')}
        </p>
      )}

      <div className="space-y-2">
        {actions.map((action) => (
          <div key={action.id} className="rounded-md border p-3 space-y-1">
            {editingId === action.id ? (
              <div className="space-y-2">
                <Textarea
                  value={commentValue}
                  onChange={(e) => setCommentValue(e.target.value)}
                  placeholder={t('reports.monthly.correctiveActions.commentLabel')}
                  rows={3}
                  disabled={submitting}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={submitting || !commentValue.trim()}
                  >
                    {t('reports.monthly.correctiveActions.save')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelForm} disabled={submitting}>
                    {t('reports.monthly.correctiveActions.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm">{action.comment}</p>
                <p className="text-xs text-muted-foreground">
                  {t('reports.monthly.correctiveActions.createdBy')}{' '}
                  {action.createdByUserName ?? '—'}
                  {' · '}
                  {formatDateTime(action.createdAt)}
                </p>
                {canEdit && (
                  <div className="flex gap-1 pt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => startEdit(action)}
                    >
                      <PencilIcon className="h-3.5 w-3.5 mr-1" />
                      {t('reports.monthly.correctiveActions.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTargetId(action.id)}
                    >
                      <Trash2Icon className="h-3.5 w-3.5 mr-1" />
                      {t('reports.monthly.correctiveActions.delete')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        {addingNew && (
          <div className="rounded-md border p-3 space-y-2">
            <Textarea
              value={commentValue}
              onChange={(e) => setCommentValue(e.target.value)}
              placeholder={t('reports.monthly.correctiveActions.commentLabel')}
              rows={3}
              disabled={submitting}
              autoFocus
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={submitting || !commentValue.trim()}>
                {t('reports.monthly.correctiveActions.save')}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelForm} disabled={submitting}>
                {t('reports.monthly.correctiveActions.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog
        open={deleteTargetId != null}
        onOpenChange={(open) => !open && setDeleteTargetId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('reports.monthly.correctiveActions.confirmDeleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('reports.monthly.correctiveActions.confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('reports.signatures.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTargetId != null && handleDelete(deleteTargetId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('reports.monthly.correctiveActions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

const AttachmentsSection = ({
  reportId,
  attachments,
  canEdit,
  canDeleteAttachment,
}: {
  reportId: number
  attachments: AttachmentItem[]
  canEdit: boolean
  canDeleteAttachment: boolean
}) => {
  const { formatDateTime } = useSiteDateTime()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [description, setDescription] = useState('')
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null)
  }

  const handleSubmit = () => {
    if (!selectedFile || uploading) return

    setUploading(true)
    const formData = new FormData()
    formData.append('file', selectedFile)
    if (description.trim()) formData.append('attachment_description', description.trim())

    router.post(reportAttachmentCreatePath(reportId), formData, {
      forceFormData: true,
      onSuccess: () => {
        setUploading(false)
        setShowUploadForm(false)
        setDescription('')
        setSelectedFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      },
      onError: () => {
        setUploading(false)
      },
    })
  }

  const handleDiscard = (id: number) => {
    router.delete(reportAttachmentDiscardPath(reportId, id), {
      onFinish: () => setDeleteTargetId(null),
    })
  }

  const cancelUpload = () => {
    setShowUploadForm(false)
    setDescription('')
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <section className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">{t('reports.monthly.attachments.title')}</h3>
        {canEdit && !showUploadForm && (
          <Button size="sm" variant="outline" onClick={() => setShowUploadForm(true)}>
            <PaperclipIcon className="h-4 w-4 mr-1" />
            {t('reports.monthly.attachments.upload')}
          </Button>
        )}
      </div>

      {showUploadForm && (
        <div className="rounded-md border p-3 space-y-3">
          <div className="space-y-1">
            <Label htmlFor="attachment-description">
              {t('reports.monthly.attachments.descriptionLabel')}
            </Label>
            <Input
              id="attachment-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            {selectedFile && (
              <span className="text-sm text-muted-foreground flex-1 truncate min-w-0">
                {selectedFile.name}
              </span>
            )}
            <Button
              size="sm"
              variant={selectedFile ? 'outline' : 'default'}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {selectedFile
                ? t('reports.monthly.attachments.changeFile')
                : t('reports.monthly.attachments.selectFile')}
            </Button>
            {selectedFile && (
              <Button size="sm" onClick={handleSubmit} disabled={uploading}>
                {uploading
                  ? t('reports.monthly.attachments.uploading')
                  : t('reports.monthly.attachments.upload')}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={cancelUpload} disabled={uploading}>
              {t('reports.monthly.attachments.cancel')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('reports.monthly.attachments.allowedFormats')}
          </p>
        </div>
      )}

      {attachments.length === 0 && !showUploadForm && (
        <p className="text-sm text-muted-foreground">{t('reports.monthly.attachments.noItems')}</p>
      )}

      <div className="space-y-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center justify-between rounded-md border p-3 gap-2"
          >
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-medium truncate">{attachment.attachmentName}</p>
              {attachment.attachmentDescription && (
                <p className="text-xs text-muted-foreground">{attachment.attachmentDescription}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formatDateTime(attachment.createdAt, attachment.timeZoneName)}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <a
                href={reportAttachmentDownloadPath(reportId, attachment.id)}
                target="_blank"
                rel="noreferrer"
              >
                <Button size="sm" variant="ghost" className="h-7 px-2">
                  <DownloadIcon className="h-3.5 w-3.5 mr-1" />
                  {t('reports.monthly.attachments.download')}
                </Button>
              </a>
              {canEdit && canDeleteAttachment && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-destructive hover:text-destructive"
                  onClick={() => setDeleteTargetId(attachment.id)}
                >
                  <Trash2Icon className="h-3.5 w-3.5 mr-1" />
                  {t('reports.monthly.attachments.delete')}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog
        open={deleteTargetId != null}
        onOpenChange={(open) => !open && setDeleteTargetId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('reports.monthly.attachments.confirmDeleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('reports.monthly.attachments.confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('reports.monthly.attachments.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTargetId != null && handleDiscard(deleteTargetId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('reports.monthly.attachments.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

// Builds the fetch callback for the InstrumentCalendarViewPanel used within a report.
// When the panel needs new calendar data (month nav, task filter), it hits the report
// show endpoint as an Inertia partial reload requesting only activeInstrumentCalendar.
const buildReportCalendarFetchFn =
  (reportId: number, instrumentId: number, pageVersion: string | null | undefined) =>
  async (
    from: string,
    to: string,
    taskIds: number[],
  ): Promise<WebInstrumentCalendarView | null> => {
    const url = new URL(reportsMonthlyShowPath({ id: reportId }), window.location.origin)
    url.searchParams.set('instrument_id', String(instrumentId))
    url.searchParams.set('calendar_from', from)
    url.searchParams.set('calendar_to', to)
    if (taskIds.length > 0) url.searchParams.set('calendar_task_ids', taskIds.join(','))

    const props = await fetchInertiaPartial<{
      activeInstrumentCalendar: WebInstrumentCalendarView
    }>(url, { component: 'Reports/Show', only: ['activeInstrumentCalendar'], version: pageVersion })
    return props?.activeInstrumentCalendar ?? null
  }

interface MaintenanceLogsSectionProps {
  reportId: number
  instruments: MaintenanceInstrument[]
  initialCalendarData: WebInstrumentCalendarView | null
  initialLots: LotItem[]
  alertCounts3month: AlertCounts[]
  hideEmptyData: boolean
  reportMonth: string | null
}

const MaintenanceLogsSection = ({
  reportId,
  instruments,
  initialCalendarData,
  initialLots,
  alertCounts3month,
  hideEmptyData,
  reportMonth,
}: MaintenanceLogsSectionProps) => {
  const { version: pageVersion } = usePage()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [calendarData, setCalendarData] = useState<WebInstrumentCalendarView | null>(
    initialCalendarData ?? null,
  )
  const [lots, setLots] = useState<LotItem[]>(initialLots)
  const [loadingInstrument, setLoadingInstrument] = useState(false)
  const [minCalendarHeight, setMinCalendarHeight] = useState<number | undefined>(undefined)
  const abortControllerRef = useRef<AbortController | null>(null)
  const calendarContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const selectedInstrument = instruments[selectedIndex] ?? null

  const loadInstrument = async (newIndex: number) => {
    const instrument = instruments[newIndex]
    if (!instrument) return

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    if (calendarContainerRef.current) {
      setMinCalendarHeight(calendarContainerRef.current.offsetHeight)
    }

    setLoadingInstrument(true)
    setCalendarData(null)
    setLots([])
    setSelectedIndex(newIndex)

    try {
      const url = new URL(reportsMonthlyShowPath({ id: reportId }), window.location.origin)
      url.searchParams.set('instrument_id', String(instrument.id))

      const props = await fetchInertiaPartial<{
        activeInstrumentCalendar: WebInstrumentCalendarView | null
        activeInstrumentLots: LotItem[]
      }>(url, {
        component: 'Reports/Show',
        only: ['activeInstrumentCalendar', 'activeInstrumentLots'],
        version: pageVersion,
        signal: controller.signal,
      })
      if (!props) return

      setCalendarData(props.activeInstrumentCalendar ?? null)
      setLots(props.activeInstrumentLots ?? [])
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      console.error('Failed to load instrument maintenance data', error)
    } finally {
      if (!controller.signal.aborted) {
        setLoadingInstrument(false)
        setMinCalendarHeight(undefined)
      }
    }
  }

  if (!selectedInstrument) {
    return (
      <section className="space-y-3 rounded-lg border p-4">
        <h3 className="font-semibold text-lg">{t('reports.monthly.maintenanceLogs.title')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('reports.monthly.maintenanceLogs.noInstruments')}
        </p>
      </section>
    )
  }

  return (
    <section className="space-y-4 rounded-lg border p-4">
      <h3 className="font-semibold text-lg">{t('reports.monthly.maintenanceLogs.title')}</h3>

      {/* Hide empty data toggle */}
      <div className="flex items-center gap-2">
        <Switch
          id="hide-empty-data"
          checked={hideEmptyData}
          onCheckedChange={(checked) => {
            router.get(
              window.location.pathname,
              { hide_empty_data: checked ? '1' : '0' },
              { preserveState: false },
            )
          }}
        />
        <Label htmlFor="hide-empty-data" className="text-sm cursor-pointer">
          {t('reports.monthly.maintenanceLogs.hideEmptyData')}
        </Label>
      </div>

      {/* Instrument pager */}
      <div className="flex items-center gap-3 rounded-md border p-3">
        <Button
          variant="ghost"
          size="icon"
          disabled={selectedIndex === 0 || loadingInstrument}
          onClick={() => loadInstrument(selectedIndex - 1)}
          aria-label={t('reports.monthly.maintenanceLogs.prevInstrument')}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{selectedInstrument.instrumentLabel}</span>
            {!selectedInstrument.canTest && (
              <span className="text-xs rounded-full bg-destructive/10 text-destructive px-2 py-0.5 shrink-0">
                {t('reports.monthly.maintenanceLogs.offlineBadge')}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground flex gap-2 flex-wrap">
            {selectedInstrument.serialNumber && <span>{selectedInstrument.serialNumber}</span>}
            {selectedInstrument.unitName && <span>{selectedInstrument.unitName}</span>}
            <span>
              {selectedIndex + 1} / {instruments.length}
            </span>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          disabled={selectedIndex === instruments.length - 1 || loadingInstrument}
          onClick={() => loadInstrument(selectedIndex + 1)}
          aria-label={t('reports.monthly.maintenanceLogs.nextInstrument')}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* 3-month alert trend */}
      {alertCounts3month.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              {t('reports.monthly.maintenanceLogs.alertTrendTitle')}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {monthlyReportTrendCategories.map((config) => (
                <span
                  key={config.key}
                  className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
                >
                  <span className={`h-2 w-2 rounded-full ${config.dotBgColorClass}`} />
                  {t(config.labelKey)}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {alertCounts3month.map((period) => (
              <div key={period.periodLabel} className="rounded-lg border bg-muted/30 p-3">
                <p className="mb-2 text-xs font-semibold text-center">{period.periodLabel}</p>
                <div className="flex items-center justify-center gap-5">
                  {monthlyReportTrendCategories.map((config) => {
                    const count = alertCountForCategory(period, config.key)
                    return (
                      <div key={config.key} className="flex flex-col items-center gap-1">
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${count > 0 ? config.dotBgColorClass : 'bg-muted-foreground/20'}`}
                        />
                        <span
                          className={`text-base font-semibold tabular-nums ${count > 0 ? '' : 'text-muted-foreground/50'}`}
                        >
                          {count}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lot summary */}
      {lots.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground font-medium">
            {t('reports.monthly.maintenanceLogs.activeLots')}
          </p>
          <div className="flex flex-wrap gap-2">
            {lots.map((lot) => (
              <span key={lot.id} className="text-xs rounded-md border px-2 py-1 bg-muted/50">
                {lot.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* NC Symbol Legend — always visible for audit clarity */}
      <NcSymbolLegend />

      {/* Calendar */}
      <div ref={calendarContainerRef} style={{ minHeight: minCalendarHeight }}>
        {loadingInstrument ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            {t('reports.monthly.maintenanceLogs.loading')}
          </div>
        ) : (
          calendarData !== null && (
            <InstrumentCalendarViewPanel
              key={selectedInstrument.id}
              instrumentId={selectedInstrument.id}
              instrumentLabel={selectedInstrument.instrumentLabel}
              serialNumber={selectedInstrument.serialNumber}
              initialCalendarData={calendarData}
              fetchCalendarData={buildReportCalendarFetchFn(
                reportId,
                selectedInstrument.id,
                pageVersion,
              )}
              hideSignOffHistory
              lockedToMonth={reportMonth}
              showTaskFrequencyDetailsControl={false}
            />
          )
        )}
      </div>
    </section>
  )
}

interface UsersSectionProps {
  reportId: number
  users: ReportUserRow[]
}

const UsersSection = ({ reportId, users }: UsersSectionProps) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = search.trim()
    ? users.filter((user) => user.displayName.toLowerCase().includes(search.toLowerCase()))
    : users

  return (
    <section className="rounded-lg border">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors">
          <h3 className="font-semibold text-lg">{t('reports.monthly.users.title')}</h3>
          <ChevronDownIcon
            className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3">
            <div className="flex items-center gap-2">
              <Input
                placeholder={t('reports.monthly.users.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
              <a
                href={reportsMonthlyUsersPdfPath({ id: reportId })}
                target="_blank"
                rel="noreferrer"
                className="ml-auto"
              >
                <Button variant="outline" size="sm">
                  <DownloadIcon className="mr-1.5 h-3.5 w-3.5" />
                  {t('reports.monthly.users.exportPdf')}
                </Button>
              </a>
            </div>

            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('reports.monthly.users.noUsers')}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50 text-left">
                      <th className="px-3 py-2 font-medium">
                        {t('reports.monthly.users.colName')}
                      </th>
                      <th className="px-3 py-2 font-medium text-right">
                        {t('reports.monthly.users.colTasksCompleted')}
                      </th>
                      <th className="px-3 py-2 font-medium text-right">
                        {t('reports.monthly.users.colLateLogs')}
                      </th>
                      <th className="px-3 py-2 font-medium text-right">
                        {t('reports.monthly.users.colAvgCompliance')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((user) => (
                      <tr key={user.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="px-3 py-2">{user.displayName}</td>
                        <td className="px-3 py-2 text-right">{user.tasksCompleted}</td>
                        <td className="px-3 py-2 text-right">{user.lateLogs}</td>
                        <td className="px-3 py-2 text-right">
                          {user.avgCompliancePercent != null
                            ? `${Math.round(user.avgCompliancePercent)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  )
}

// ── Sign-off Audit Trail ─────────────────────────────────────────────────────
interface SignOffAuditTrailProps {
  entries: SignOffHistoryEntry[]
}

const SignOffAuditTrail = ({ entries }: SignOffAuditTrailProps) => {
  if (entries.length === 0) {
    return (
      <div className="pt-2 border-t space-y-2">
        <h4 className="text-sm font-semibold">{t('reports.signatures.historyTitle')}</h4>
        <p className="text-sm text-muted-foreground">{t('reports.signatures.historyEmpty')}</p>
      </div>
    )
  }

  return (
    <div className="pt-2 border-t space-y-2">
      <h4 className="text-sm font-semibold">{t('reports.signatures.historyTitle')}</h4>
      <div className="space-y-1.5">
        {entries.map((entry) => (
          <SignOffAuditEntry key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  )
}

const SignOffAuditEntry = ({ entry }: { entry: SignOffHistoryEntry }) => {
  const { formatDateTime } = useSiteDateTime()
  const timestamp = formatDateTime(entry.occurredAt)

  const label = entryLabel(entry)
  const isStruck = entry.isUndo || (!entry.isValid && entry.isSigned)

  return (
    <div
      className={`flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs ${isStruck ? 'line-through opacity-60' : ''}`}
    >
      <span className="font-medium">{entry.userName}</span>
      <span className={labelColor(entry)}>{label}</span>
      <span className="text-muted-foreground">{timestamp}</span>
      {entry.comments && (
        <span className="basis-full text-muted-foreground italic pl-0">
          &ldquo;{entry.comments}&rdquo;
        </span>
      )}
    </div>
  )
}

const entryLabel = (entry: SignOffHistoryEntry): string => {
  if (entry.isUndo) return t('reports.signatures.undone')
  if (!entry.isSigned && !entry.isUndo) return t('reports.signatures.rejected')
  if (entry.isSigned && !entry.isValid) return t('reports.signatures.invalidated')
  return t('reports.signatures.signed')
}

const labelColor = (entry: SignOffHistoryEntry): string => {
  if (entry.isUndo) return 'text-muted-foreground'
  if (!entry.isSigned) return 'text-destructive'
  if (!entry.isValid) return 'text-muted-foreground'
  return 'text-green-600'
}

const EXPORT_STATUS_POLL_INTERVAL_MS = 3000

const ReportsShowPage = () => {
  const { formatDateTime } = useSiteDateTime()
  const {
    report,
    chainSigners,
    canSign,
    canUndo,
    canAddCorrectiveAction,
    canUploadAttachment,
    derivedStatus,
    exportStatuses,
    hasConfidentialPdf,
    canViewConfidential,
    complianceTrend,
    alertCountsCurrent,
    alertCounts3month,
    licenseSummary,
    unitsInstrumentsSummary,
    correctiveActions,
    attachments,
    canDeleteAttachment,
    maintenanceInstruments,
    activeInstrumentCalendar,
    activeInstrumentLots,
    reportUsers,
    rejectionAlert,
    signOffHistory,
    hideEmptyData,
  } = usePage<Props>().props
  const { currentUserInfo } = usePage<SharedProps>().props
  const hasUserPin = currentUserInfo?.hasUserPin ?? false
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectionAlertDismissed, setRejectionAlertDismissed] = useState(false)
  const [signDialogOpen, setSignDialogOpen] = useState(false)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Auto-download bookkeeping for the live-report regenerate flow (REBUILD-182): when the
  // user (re)generates on a live report we must download the *fresh* file once its build
  // finishes — not the pre-existing one. `format` is what the user asked for; `sawInFlight`
  // flips true only after that format is observed pending/generating, so a live report's
  // ever-present `stale` state cannot trigger a premature download before regeneration.
  const autoDownloadRef = useRef<{ format: 'pdf' | 'xlsx'; sawInFlight: boolean } | null>(null)

  const isAnyExportInFlight =
    exportStatuses.pdf === 'pending' ||
    exportStatuses.pdf === 'generating' ||
    exportStatuses.xlsx === 'pending' ||
    exportStatuses.xlsx === 'generating'

  useEffect(() => {
    if (isAnyExportInFlight) {
      pollTimerRef.current = setInterval(() => {
        router.reload({
          only: ['exportStatuses', 'pdfReady', 'hasConfidentialPdf'],
        })
      }, EXPORT_STATUS_POLL_INTERVAL_MS)
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [isAnyExportInFlight])

  const pdfDownloadHref = `${reportsMonthlyDownloadPdfPath({ id: report.id })}?confidential=${
    canViewConfidential ? '1' : '0'
  }`
  const xlsxDownloadHref = `${reportsMonthlyDownloadXlsxPath({ id: report.id })}?confidential=${
    canViewConfidential ? '1' : '0'
  }`
  // request_export stamps is_confidential from the requester's capability, so the freshly
  // regenerated file that auto-download must fetch is confidential for a capable user. Both
  // the static download link above and auto-download target the copy matching the user's
  // capability — without confidential=1 a capable user's download looks for a redacted row
  // that request_export never created (same fix already applied to xlsxDownloadHref).
  const pdfAutoDownloadHref = `${reportsMonthlyDownloadPdfPath({ id: report.id })}?confidential=${
    canViewConfidential ? '1' : '0'
  }`

  // Auto-download the freshly generated file once its regeneration finishes. The download
  // only fires after the requested format has been seen in-flight (pending/generating) and
  // has then settled into a downloadable state (`ready` for frozen, `stale` for live), so a
  // live report's baseline `stale` status never downloads a not-yet-regenerated file. A ref
  // (not state) keeps navigation a pure side effect without a cascading re-render.
  useEffect(() => {
    const pending = autoDownloadRef.current
    if (pending === null) return

    const status = exportStatuses[pending.format]

    if (status === 'pending' || status === 'generating') {
      pending.sawInFlight = true
      return
    }

    if (!pending.sawInFlight) return
    if (status !== 'ready' && status !== 'stale') return

    autoDownloadRef.current = null
    window.location.assign(pending.format === 'pdf' ? pdfAutoDownloadHref : xlsxDownloadHref)
  }, [exportStatuses, pdfAutoDownloadHref, xlsxDownloadHref])

  const handleRequestPdf = () => {
    if (derivedStatus.live) autoDownloadRef.current = { format: 'pdf', sawInFlight: false }
    router.post(reportsMonthlyRequestPdfPath({ id: report.id }), {})
  }

  const handleRequestXlsx = () => {
    if (derivedStatus.live) autoDownloadRef.current = { format: 'xlsx', sawInFlight: false }
    router.post(reportsMonthlyRequestXlsxPath({ id: report.id }), {})
  }

  useBreadcrumbs([
    { label: t('nav.monthlyReports'), href: reportsMonthlyPath() },
    { label: report.departmentName ?? '—' },
  ])

  const compliancePercent = report.compliancePercent != null ? `${report.compliancePercent}%` : '—'

  const handleUndoSign = () => {
    router.post(reportsMonthlyUndoSignPath({ id: report.id }))
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Rejection-alert banner */}
      {rejectionAlert && !rejectionAlertDismissed && (
        <Alert variant="destructive">
          <AlertTitle className="flex items-center justify-between">
            <span>{t('reports.signatures.rejectionAlertTitle')}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-destructive hover:text-destructive"
              onClick={() => setRejectionAlertDismissed(true)}
            >
              {t('reports.signatures.rejectionAlertDismiss')}
            </Button>
          </AlertTitle>
          <AlertDescription className="space-y-1 mt-1">
            {rejectionAlert.rejectedBy && (
              <p className="text-sm font-medium">{rejectionAlert.rejectedBy}</p>
            )}
            {rejectionAlert.comments && <p className="text-sm">{rejectionAlert.comments}</p>}
            <p className="text-xs opacity-75">{formatDateTime(rejectionAlert.rejectedAt)}</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Sticky report header */}
      <div className="sticky top-0 z-20 bg-background border-b pb-4 space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-bold">{report.departmentName ?? '—'}</h2>
          <ReportStatusBadge record={{ ...report, derivedStatus }} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {report.siteName && <span>{report.siteName}</span>}
          <span>{report.reportStartDateFormatted}</span>
          <span>
            <span className="font-medium text-foreground">
              {t('reports.monthly.compliancePercent')}:{' '}
            </span>
            {compliancePercent}
          </span>
          {derivedStatus.live && <ReportLiveIndicator />}
        </div>

        {/* Export action buttons */}
        <div className="flex flex-wrap gap-2 pt-2">
          {/* Single Download dropdown consolidating PDF + Excel exports */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {exportStatuses.pdf === 'pending' ||
                exportStatuses.pdf === 'generating' ||
                exportStatuses.xlsx === 'pending' ||
                exportStatuses.xlsx === 'generating' ? (
                  <LoaderCircleIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <DownloadIcon className="h-3.5 w-3.5 mr-1" />
                )}
                {t('reports.monthly.exports.download')}
                <ChevronDownIcon className="h-3.5 w-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {/* PDF item */}
              {exportStatuses.pdf === 'ready' ? (
                <DropdownMenuItem asChild>
                  <a href={pdfDownloadHref} rel="noreferrer">
                    {t('reports.monthly.exports.pdf')}
                  </a>
                </DropdownMenuItem>
              ) : exportStatuses.pdf === 'pending' || exportStatuses.pdf === 'generating' ? (
                <DropdownMenuItem disabled>
                  <LoaderCircleIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                  {t('reports.monthly.exports.generatingInProgress', {
                    format: t('reports.monthly.exports.pdf'),
                  })}
                </DropdownMenuItem>
              ) : exportStatuses.pdf === 'failed' ? (
                <DropdownMenuItem onClick={handleRequestPdf}>
                  <AlertCircleIcon className="h-3.5 w-3.5 mr-1 text-destructive" />
                  {t('reports.monthly.exports.pdf')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleRequestPdf}>
                  {t('reports.monthly.exports.pdf')}
                </DropdownMenuItem>
              )}

              {/* Excel item */}
              {exportStatuses.xlsx === 'ready' ? (
                <DropdownMenuItem asChild>
                  {/* Match request_export's is_confidential stamp (user capability).
                      Without confidential=1, download looks for a redacted row that was never created. */}
                  <a href={xlsxDownloadHref}>{t('reports.monthly.exports.excel')}</a>
                </DropdownMenuItem>
              ) : exportStatuses.xlsx === 'pending' || exportStatuses.xlsx === 'generating' ? (
                <DropdownMenuItem disabled>
                  <LoaderCircleIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                  {t('reports.monthly.exports.generatingInProgress', {
                    format: t('reports.monthly.exports.excel'),
                  })}
                </DropdownMenuItem>
              ) : exportStatuses.xlsx === 'failed' ? (
                <DropdownMenuItem onClick={handleRequestXlsx}>
                  <AlertCircleIcon className="h-3.5 w-3.5 mr-1 text-destructive" />
                  {t('reports.monthly.exports.excel')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleRequestXlsx}>
                  {t('reports.monthly.exports.excel')}
                </DropdownMenuItem>
              )}

              {/* PDF (Full) — only when confidential PDF is ready */}
              {exportStatuses.pdf === 'ready' && canViewConfidential && hasConfidentialPdf && (
                <DropdownMenuItem asChild>
                  <a
                    href={`${reportsMonthlyDownloadPdfPath({ id: report.id })}?confidential=1`}
                    rel="noreferrer"
                  >
                    {t('reports.monthly.exports.downloadPdfFull')}
                  </a>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {canSign && (
            <Button size="sm" onClick={() => setSignDialogOpen(true)}>
              {t('reports.signatures.signNow')}
            </Button>
          )}
        </div>
      </div>

      {/* Summary — 3-Month Compliance & QA Alerts */}
      {(complianceTrend || alertCountsCurrent) && (
        <section className="rounded-lg border">
          <div className="flex items-center gap-2 border-b px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('reports.monthly.summary')}
            </span>
          </div>

          <div className="flex flex-col gap-6 p-4 lg:flex-row lg:items-start lg:gap-8">
            {complianceTrend && complianceTrend.monthlyBars.length > 0 && (
              <div className="shrink-0 space-y-2.5">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('reports.monthly.trends.threeMonthCompliance')}
                </span>
                <div className="flex gap-2.5">
                  {complianceTrend.monthlyBars.map((bar, index) => (
                    <MonthComplianceCard
                      key={bar.periodLabel}
                      periodLabel={bar.periodLabel}
                      compliancePercent={bar.compliancePercent}
                      isCurrent={index === complianceTrend.monthlyBars.length - 1}
                    />
                  ))}
                </div>
              </div>
            )}

            {complianceTrend && complianceTrend.monthlyBars.length > 0 && alertCountsCurrent && (
              <div className="hidden self-stretch border-l lg:block" />
            )}

            {alertCountsCurrent && (
              <div className="min-w-0 flex-1 space-y-2.5">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('reports.monthly.alerts.titleForPeriod', {
                    period: alertCountsCurrent.periodLabel,
                  })}
                </span>
                <div
                  className="flex flex-wrap items-center gap-2"
                  aria-label={t('reports.monthly.alerts.title')}
                >
                  {monthlyReportAlertCategories.map((config) => (
                    <QaAlertBadge
                      key={config.key}
                      category={config.key}
                      label={t(config.labelKey)}
                      count={alertCountForCategory(alertCountsCurrent, config.key)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Detail rows table */}
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-lg">{t('reports.monthly.reportDetail')}</h3>
        </div>

        {report.detailRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('reports.monthly.noDetailRows')}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="px-3 py-2 font-medium">{t('reports.monthly.detailInstrument')}</th>
                  <th className="px-3 py-2 font-medium">{t('reports.monthly.detailTask')}</th>
                  <th className="px-3 py-2 font-medium">{t('reports.monthly.detailType')}</th>
                  <th className="px-3 py-2 font-medium">{t('reports.monthly.detailUnit')}</th>
                  <th className="px-3 py-2 font-medium text-right whitespace-nowrap">
                    {t('reports.monthly.detailCompliance')}
                  </th>
                  <th className="px-3 py-2 font-medium text-right">
                    {t('reports.monthly.detailConfirmed')}
                  </th>
                  <th className="px-3 py-2 font-medium text-right">
                    {t('reports.monthly.detailOverrides')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('reports.monthly.detailLevel')}</th>
                </tr>
              </thead>
              <tbody>
                {report.detailRows.map((row) => (
                  <tr key={row.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2">{row.instrumentLabel ?? '—'}</td>
                    <td className="px-3 py-2">{row.taskName ?? '—'}</td>
                    <td className="px-3 py-2">{row.typeName ?? '—'}</td>
                    <td className="px-3 py-2">{row.unitName ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {row.compliancePercent != null ? `${row.compliancePercent}%` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{row.complianceConfirmCount ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{row.complianceOverrideCount ?? '—'}</td>
                    <td className="px-3 py-2">{row.reportLevel ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* License Summary section — collapsed by default */}
      {licenseSummary && licenseSummary.length > 0 && (
        <LicenseSummarySection licenseSummary={licenseSummary} />
      )}

      {/* Unit List — simple compliance table */}
      {unitsInstrumentsSummary && unitsInstrumentsSummary.length > 0 && (
        <UnitListSection unitsInstrumentsSummary={unitsInstrumentsSummary} />
      )}

      <CorrectiveActionsSection
        reportId={report.id}
        actions={correctiveActions}
        canEdit={canAddCorrectiveAction}
      />

      {maintenanceInstruments != null && (
        <MaintenanceLogsSection
          reportId={report.id}
          instruments={maintenanceInstruments}
          initialCalendarData={activeInstrumentCalendar ?? null}
          initialLots={activeInstrumentLots ?? []}
          alertCounts3month={alertCounts3month ?? []}
          hideEmptyData={hideEmptyData}
          reportMonth={report.reportStartDate?.slice(0, 7) ?? null}
        />
      )}

      <UsersSection reportId={report.id} users={reportUsers ?? []} />

      <AttachmentsSection
        reportId={report.id}
        attachments={attachments}
        canEdit={canUploadAttachment}
        canDeleteAttachment={canDeleteAttachment}
      />

      {chainSigners.length > 0 && (
        <section className="space-y-4 rounded-lg border p-4">
          <h3 className="font-semibold text-lg">{t('reports.signatures.sectionTitle')}</h3>

          <SignersChain signers={chainSigners} />

          {canSign && (
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={() => setSignDialogOpen(true)}>
                {t('reports.signatures.sign')}
              </Button>

              <Button variant="destructive" size="sm" onClick={() => setRejectOpen(true)}>
                {t('reports.signatures.reject')}
              </Button>
            </div>
          )}

          {canUndo && (
            <div className="pt-1">
              <Button variant="outline" size="sm" onClick={handleUndoSign}>
                {t('reports.signatures.undo')}
              </Button>
            </div>
          )}

          <SignOffAuditTrail entries={signOffHistory} />
        </section>
      )}

      {rejectOpen && (
        <ReportRejectModal reportId={report.id} onClose={() => setRejectOpen(false)} />
      )}

      {canSign && (
        <SignReportDialog
          reportId={report.id}
          open={signDialogOpen}
          onOpenChange={setSignDialogOpen}
          hasUserPin={hasUserPin}
          returnTo="show"
        />
      )}
    </div>
  )
}

export default ReportsShowPage
